import { apiRequest } from "@/lib/api";

export type PublicAnalyticsEvent =
  | "store_view"
  | "product_view"
  | "whatsapp_click"
  | "marketplace_impression"
  | "marketplace_click"
  | "search_impression";

type TrackSingleEventInput = {
  event: Exclude<
    PublicAnalyticsEvent,
    "search_impression"
  >;
  company: string;
  product?: number;
  metadata?: Record<string, unknown>;
};

type TrackSearchImpressionInput = {
  event: "search_impression";
  companies: string[];
  metadata?: Record<string, unknown>;
};

type TrackEventInput =
  | TrackSingleEventInput
  | TrackSearchImpressionInput;

export type MarketplaceAttribution = {
  sponsored?: boolean;
  campaign_id?: string;
  campaign_placement?: string;
};

// Attribution follows this navigation only; direct/organic links have no campaign.
export function getMarketplaceAttribution(): MarketplaceAttribution {
  if (typeof window === "undefined") return { sponsored: false };
  const params = new URLSearchParams(window.location.search);
  const campaign_id = params.get("campaign");
  const campaign_placement = params.get("placement");
  if (campaign_id && /^[0-9a-f-]{36}$/i.test(campaign_id)
    && campaign_placement === "marketplace_featured") {
    return { sponsored: true, campaign_id, campaign_placement };
  }
  return { sponsored: false };
}

function marketplaceMetadata(attribution: MarketplaceAttribution = {}) {
  return { ...attribution, sponsored: Boolean(attribution.sponsored),
    placement_type: attribution.sponsored ? "sponsored" : "organic" };
}

export async function trackAnalyticsEvent(
  input: TrackEventInput,
): Promise<void> {
  try {
    await apiRequest<{ registered: number }>(
      "/analytics/events/",
      {
        method: "POST",
        body: JSON.stringify(input),
      },
    );
  } catch {
    // Analytics nunca deve bloquear
    // navegação, contato ou uso da loja.
  }
}

export function getAnalyticsSessionId(): string {
  const key = "fornecedor_analytics_session_id";

  try {
    const existing =
      sessionStorage.getItem(key);

    if (existing) {
      return existing;
    }

    const sessionId =
      typeof crypto !== "undefined" &&
      "randomUUID" in crypto
        ? crypto.randomUUID()
        : (
            `${Date.now()}-` +
            Math.random()
              .toString(36)
              .slice(2)
          );

    sessionStorage.setItem(
      key,
      sessionId,
    );

    return sessionId;
  } catch {
    return (
      `${Date.now()}-` +
      Math.random()
        .toString(36)
        .slice(2)
    );
  }
}

export function trackStoreViewOncePerSession(
  slug: string,
): void {
  if (typeof window === "undefined") {
    return;
  }

  const storageKey =
    `fornecedor_analytics_store_view_${slug}`;

  try {
    if (sessionStorage.getItem(storageKey)) {
      return;
    }

    sessionStorage.setItem(storageKey, "1");
  } catch {
    // Continua registrando mesmo se o
    // sessionStorage estiver indisponível.
  }

  void trackAnalyticsEvent({
    event: "store_view",
    company: slug,
    metadata: {
      source: "store",
      session_id: getAnalyticsSessionId(),
    },
  });
}

export function trackProductViewOncePerSession(
  companySlug: string,
  productId: number,
): void {
  if (typeof window === "undefined") {
    return;
  }

  const storageKey =
    `fornecedor_analytics_product_view_${companySlug}_${productId}`;

  try {
    if (sessionStorage.getItem(storageKey)) {
      return;
    }

    sessionStorage.setItem(storageKey, "1");
  } catch {
    // Continua registrando mesmo se o
    // sessionStorage estiver indisponível.
  }

  void trackAnalyticsEvent({
    event: "product_view",
    company: companySlug,
    product: productId,
    metadata: {
      source: "store",
      session_id: getAnalyticsSessionId(),
    },
  });
}


function trackMarketplaceEventOncePerSession(
  event: "marketplace_impression" | "marketplace_click",
  companySlug: string,
  productId: number,
  attribution: MarketplaceAttribution = {},
): void {
  if (typeof window === "undefined") {
    return;
  }

  const storageKey =
    `fornecedor_analytics_${event}_${companySlug}_${productId}_${attribution.campaign_id ?? "organic"}_${attribution.campaign_placement ?? ""}`;

  try {
    if (sessionStorage.getItem(storageKey)) {
      return;
    }

    sessionStorage.setItem(storageKey, "1");
  } catch {
    // Se sessionStorage estiver indisponivel,
    // o evento continua sendo registrado.
  }

  void trackAnalyticsEvent({
    event,
    company: companySlug,
    product: productId,
    metadata: {
      source: "marketplace",
      ...marketplaceMetadata(attribution),
      session_id: getAnalyticsSessionId(),
    },
  });
}

export function trackMarketplaceImpressionOncePerSession(
  companySlug: string,
  productId: number,
  attribution: MarketplaceAttribution = {},
): void {
  trackMarketplaceEventOncePerSession(
    "marketplace_impression",
    companySlug,
    productId,
    attribution,
  );
}

export function trackMarketplaceClickOncePerSession(
  companySlug: string,
  productId: number,
  attribution: MarketplaceAttribution = {},
): void {
  trackMarketplaceEventOncePerSession(
    "marketplace_click",
    companySlug,
    productId,
    attribution,
  );
}

export function trackMarketplaceProductViewOncePerSession(
  companySlug: string,
  productId: number,
): void {
  if (typeof window === "undefined") {
    return;
  }

  const attribution = getMarketplaceAttribution();
  const storageKey =
    `fornecedor_analytics_marketplace_product_view_${companySlug}_${productId}_${attribution.campaign_id ?? "organic"}_${attribution.campaign_placement ?? ""}`;

  try {
    if (sessionStorage.getItem(storageKey)) {
      return;
    }

    sessionStorage.setItem(storageKey, "1");
  } catch {
    // Continua registrando mesmo sem sessionStorage.
  }

  void trackAnalyticsEvent({
    event: "product_view",
    company: companySlug,
    product: productId,
    metadata: {
      source: "marketplace",
      ...marketplaceMetadata(attribution),
      session_id: getAnalyticsSessionId(),
    },
  });
}

export function trackWhatsappClick(
  slug: string,
  source = "store",
  productId?: number,
): void {
  void trackAnalyticsEvent({
    event: "whatsapp_click",
    company: slug,
    ...(productId ? { product: productId } : {}),
    metadata: {
      source,
      ...(source === "marketplace" ? marketplaceMetadata(getMarketplaceAttribution()) : {}),
    },
  });
}

export function trackSearchImpressions(
  slugs: string[],
  search: string,
): void {
  const companies = Array.from(
    new Set(
      slugs
        .map((slug) => slug.trim())
        .filter(Boolean),
    ),
  );

  if (companies.length === 0) {
    return;
  }

  void trackAnalyticsEvent({
    event: "search_impression",
    companies,
    metadata: {
      search,
      source: "supplier_search",
    },
  });
}
